import { ref, computed, onBeforeUnmount } from 'vue'

// Positioning and show/hide timing for the app's hover tooltip, split from
// the markup so it works for triggers we don't render ourselves. `Tooltip.vue`
// wraps a slot in it; `quill_editor.vue` points it at whichever toolbar button
// Quill rendered under the cursor. Pair it with `TooltipBubble.vue`.

const GAP = 8 // between the trigger and the bubble
const HALF_MAX_WIDTH = 120 // half of the bubble's max-w-[240px], for viewport clamping
const FLIP_THRESHOLD = 44 // not enough room above? drop underneath instead

// `delay` is the milliseconds of hover before it appears. Long enough that
// sweeping the cursor across a dense list or toolbar doesn't strobe a tooltip
// per item, short enough that deliberately resting on an icon feels immediate.
export function useTooltip({ delay = 140 } = {}) {
  const visible = ref(false)
  const placement = ref('top')
  const coords = ref({ left: 0, top: 0 })
  const arrowOffset = ref(0)

  let showTimer = null

  function place(el) {
    const rect = el.getBoundingClientRect()
    const triggerCenter = rect.left + rect.width / 2

    // Clamped so a tooltip on an icon near either edge stays fully on screen.
    const left = Math.min(
      Math.max(triggerCenter, HALF_MAX_WIDTH + GAP),
      window.innerWidth - HALF_MAX_WIDTH - GAP,
    )

    // The arrow follows the trigger even when the bubble itself was pushed
    // inward by that clamp, so it keeps pointing at what it describes.
    arrowOffset.value = triggerCenter - left

    placement.value = rect.top < FLIP_THRESHOLD ? 'bottom' : 'top'
    coords.value = {
      left,
      top: placement.value === 'top' ? rect.top - GAP : rect.bottom + GAP,
    }
  }

  const bubbleStyle = computed(() => ({
    left: `${coords.value.left}px`,
    top: `${coords.value.top}px`,
    transform: placement.value === 'top'
      ? 'translate(-50%, -100%)'
      : 'translate(-50%, 0)',
  }))

  const arrowStyle = computed(() => ({
    left: `calc(50% + ${arrowOffset.value}px)`,
    marginLeft: '-4px',
    ...(placement.value === 'top' ? { bottom: '-3px' } : { top: '-3px' }),
  }))

  function showNow(el) {
    clearTimeout(showTimer)
    if (!el) return
    place(el)
    visible.value = true
    // Scrolling would leave the bubble stranded where the trigger used to be.
    // Recomputing on every scroll frame is wasted work for something the user
    // is no longer looking at, so it just goes away.
    window.addEventListener('scroll', hide, { capture: true, passive: true })
    window.addEventListener('resize', hide, { passive: true })
  }

  function scheduleShow(el) {
    clearTimeout(showTimer)
    showTimer = setTimeout(() => showNow(el), delay)
  }

  function hide() {
    clearTimeout(showTimer)
    visible.value = false
    window.removeEventListener('scroll', hide, { capture: true })
    window.removeEventListener('resize', hide)
  }

  onBeforeUnmount(hide)

  return { visible, bubbleStyle, arrowStyle, showNow, scheduleShow, hide }
}
